import { useQuery } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import { buttonClasses } from '@/components/ui/Button'
import { Badge, CloseButton, Drawer, ErrorBox } from '@/components/ui/Primitives'
import {
  ATS_LABEL,
  MARKET_FLAG,
  experienceLabel,
  formatDate,
  formatDateTime,
  formatSalary,
  humanise,
  relativeTime,
} from '@/lib/utils'

export function JobDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const job = useQuery({ queryKey: ['jobs', id], queryFn: () => api.job(id) })
  const j = job.data

  // Descriptions are third-party HTML: sanitise before rendering.
  const html = useMemo(
    () =>
      j?.description_html
        ? DOMPurify.sanitize(j.description_html, { FORBID_TAGS: ['style', 'img', 'iframe', 'form'], FORBID_ATTR: ['style'] })
        : null,
    [j?.description_html],
  )

  return (
    <Drawer onClose={onClose}>
      <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
        <div className="min-w-0">
          {j ? (
            <>
              <h2 className="text-[19px] font-semibold leading-snug">{j.title}</h2>
              <p className="mt-0.5 text-[14px] text-ink-muted">
                {j.company_name}
                {j.department ? ` · ${j.department}` : ''}
              </p>
            </>
          ) : (
            <div className="skeleton h-6 w-72" />
          )}
        </div>
        <CloseButton onClick={onClose} />
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        {job.isError && <ErrorBox error={job.error} />}
        {j && (
          <>
            <div className="mb-5 flex flex-wrap gap-2">
              {!j.is_active && <Badge tone="danger">Closed {j.closed_at ? relativeTime(j.closed_at) : ''}</Badge>}
              {j.market && (
                <Badge>
                  {MARKET_FLAG[j.market]} {j.market === 'IN' ? 'India' : 'USA'}
                </Badge>
              )}
              {!j.initial_import && <Badge tone="success">New opening</Badge>}
              <Badge tone="info">{ATS_LABEL[j.ats_type]}</Badge>
              {j.is_remote && <Badge tone="success">Remote</Badge>}
            </div>

            <dl className="mb-6 grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
              <Row label="Function">{j.job_function ?? '—'}</Row>
              <Row label="Seniority">{j.seniority ?? '—'}</Row>
              <Row label="Experience">{experienceLabel(j.experience_min_years, j.experience_max_years) ?? '—'}</Row>
              <Row label="Education">{j.education ?? '—'}</Row>
              <Row label="Location">
                {j.location ?? '—'}
                {j.locations.length > 1 && (
                  <span className="block text-[12.5px] text-ink-muted">+{j.locations.length - 1} more: {j.locations.slice(1, 4).join('; ')}</span>
                )}
              </Row>
              <Row label="Country">{j.country ?? j.country_code ?? '—'}</Row>
              <Row label="Employment">{j.employment_type ?? '—'}</Row>
              <Row label="Workplace">{j.workplace_type ? humanise(j.workplace_type) : '—'}</Row>
              {j.experience_level && <Row label="Experience (as posted)">{j.experience_level}</Row>}
              <Row label="Pay">{formatSalary(j.salary_min, j.salary_max, j.salary_currency, j.salary_period) ?? '—'}</Row>
              <Row label="Openings">{j.positions_count ?? '—'}</Row>
              <Row label="Requisition id">{j.requisition_id ? <span className="font-mono text-[12px]">{j.requisition_id}</span> : '—'}</Row>
              <Row label="Created on the career site">{formatDate(j.source_created_at)}</Row>
              <Row label="Posted">{formatDate(j.posted_at)}</Row>
              <Row label="First found">
                {formatDateTime(j.first_seen_at)}
                <span className="block text-[12px] text-ink-muted">{j.initial_import ? 'at the first check (baseline)' : 'new since the previous check'}</span>
              </Row>
              <Row label="Last changed">{formatDateTime(j.updated_at)}</Row>
              {j.team && <Row label="Team">{j.team}</Row>}
              <Row label="Posting id">
                <span className="break-all font-mono text-[12px]">{j.external_id}</span>
              </Row>
            </dl>

            {j.skills.length > 0 && (
              <>
                <h3 className="label-eyebrow mb-2">Skills</h3>
                <div className="mb-6 flex flex-wrap gap-1.5">
                  {j.skills.map((skill) => (
                    <span key={skill} className="rounded-pill border border-line bg-surface-muted px-2.5 py-1 text-[12.5px]">
                      {skill}
                    </span>
                  ))}
                </div>
              </>
            )}
            <Section title="Responsibilities" text={j.responsibilities} />
            <Section title="Qualifications & requirements" text={j.qualifications} />
            <Section title="Benefits" text={j.benefits} />

            <h3 className="label-eyebrow mb-2">Full description</h3>
            {html ? (
              <div className="prose-job" dangerouslySetInnerHTML={{ __html: html }} />
            ) : j.description_text ? (
              <div className="prose-job whitespace-pre-line">{j.description_text}</div>
            ) : (
              <p className="text-[13.5px] text-ink-muted">This career page does not publish a description in a readable form — open the posting.</p>
            )}
          </>
        )}
      </div>

      {j && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-6 py-3.5">
          <Link to={`/companies/${j.company_id}`} className="text-[13px] text-ink-muted hover:text-ink">
            {j.company_name} — company page →
          </Link>
          <div className="flex gap-2">
            {j.job_url && (
              <a href={j.job_url} target="_blank" rel="noreferrer" className={buttonClasses('secondary')}>
                View posting
              </a>
            )}
            {j.apply_url && (
              <a href={j.apply_url} target="_blank" rel="noreferrer" className={buttonClasses()}>
                Apply
              </a>
            )}
          </div>
        </div>
      )}
    </Drawer>
  )
}

function Section({ title, text }: { title: string; text: string | null }) {
  if (!text) return null
  return (
    <section className="mb-6">
      <h3 className="label-eyebrow mb-2">{title}</h3>
      <div className="prose-job whitespace-pre-line rounded-field bg-surface-muted/60 px-4 py-3">{text}</div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12px] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  )
}
